import { PermissionRoles } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { IAccount } from '@site-mate/sitemate-global-shared/lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { SettingsFormSkeleton } from '@/components/settings/settings-form-skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { getSitemateStartAccountQueryOptions, sitemateStartAccountKeys } from '@/queries/account';
import { accountService } from '@/services/global-api/account.service';
import { useUserStore } from '@/stores/user.store';

const formSchema = z.object({
  accountName: z.string().min(3),
});

function SettingsSitemateStartGeneralPage() {
  const { org } = useUserStore();

  const { data: account, isLoading } = useQuery(getSitemateStartAccountQueryOptions({ accountId: org?.accountId }));

  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">General</h1>
          {(isLoading || !account) && <SettingsFormSkeleton rows={2} />}
          {account && <SettingsSitemateStartGeneralForm account={account} />}
        </div>
      </ScrollArea>
    </div>
  );
}

function SettingsSitemateStartGeneralForm({ account }: { account: IAccount }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useUserStore();

  const formInstance = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountName: account.name,
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isDirty },
  } = formInstance;

  const { mutateAsync: updateAccount } = useMutation({
    mutationFn: accountService.updateAccount,
    onSuccess: async () => {
      toast.success('Account updated successfully');
      await queryClient.invalidateQueries({
        queryKey: sitemateStartAccountKeys.detail(account._id),
      });
    },
    onError: () => {
      toast.error('Failed to update account');
    },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    await updateAccount({
      _id: account._id,
      name: data.accountName,
    });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <Controller
        control={control}
        name="accountName"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Sitemate Start account name</div>
            <Input
              placeholder="Enter Sitemate Start account name..."
              value={value}
              onChange={(e) => onChange(e.currentTarget.value)}
              error={!!error}
              className="flex-1"
              disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Label className="flex items-center gap-1">
        <div className="w-1/3 shrink-0">Sitemate Start account ID</div>
        <Input value={account._id} disabled placeholder="Sitemate Start account ID" className="flex-1" />
      </Label>

      {user?.roles.includes(PermissionRoles.SitemateSuperUser) && (
        <div className="col-12">
          <Button type="submit" variant="success" className="mt-2" disabled={!isValid || isSubmitting || !isDirty} loading={isSubmitting}>
            Save changes
          </Button>
        </div>
      )}
    </form>
  );
}

export default SettingsSitemateStartGeneralPage;
